import { useCallback, useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { headingErrorState } from '../../../Reporter/state';
import type { SlateContent } from '../../../Reporter/types';
import { hasDuplicateHeadingsInNode } from './utils';

// This is the source of truth for submit/save gating and warning visibility.
// It intentionally derives from content (not headingError marks) so validity does
// not depend on plugin normalization timing.
// RP-6009 (M6): when headings are decorated paragraphs, the caller passes a label set
// so duplicate detection works off paragraph labels; without it, node-only (unchanged).
export const useSyncHeadingErrorState = (
  content: SlateContent,
  headingLabelSet?: Set<string>
): ((content: SlateContent) => void) => {
  const [, setHasHeadingError] = useRecoilState(headingErrorState);

  const syncHeadingErrorState = useCallback(
    (nextContent: SlateContent) => {
      const nextHasHeadingError = hasDuplicateHeadingsInNode(
        { children: nextContent },
        headingLabelSet
      );
      setHasHeadingError((previousHasHeadingError) =>
        previousHasHeadingError === nextHasHeadingError
          ? previousHasHeadingError
          : nextHasHeadingError
      );
    },
    [setHasHeadingError, headingLabelSet]
  );

  useEffect(() => {
    syncHeadingErrorState(content);
  }, [content, syncHeadingErrorState]);

  return syncHeadingErrorState;
};
